import DialogContentText from '@mui/material/DialogContentText';
import FormControl from '@mui/material/FormControl';
import { ChangeEvent } from 'react';
import * as React from 'react';
import { FormControlLabel, Radio, RadioGroup, Snackbar } from '@mui/material';
import { TextV2, Alert, Dialog } from '@insights-ltd/design-library/components';
import { getAvailableStatusTransitions, JobStatus } from 'variables';
import { useEditJob } from 'api/hooks';
import { IJob } from 'types/job';
import { JobsStatus } from 'components/Jobs/Table/JobsStatus';
import { useJobContext } from 'pages/Job/hooks/jobContext';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

type JobStatusDialogProps = {
  job: IJob;
};

const JobStatusDialog = ({ job }: JobStatusDialogProps) => {
  const {
    jobStatusDialog: { open, closeDialog: onClose },
  } = useJobContext();
  const {
    mutate: editJob,
    status: editJobStatus,
    reset: resetEditJob,
  } = useEditJob();

  const onSubmit = (jobStatus: JobStatus) => {
    const { deadlineDate, description, id, owner, translator } = job;
    return editJob({
      deadlineDate,
      description,
      id,
      owner,
      translator,
      status: jobStatus,
    });
  };
  const [status, setStatus] = React.useState<JobStatus | undefined>(undefined);

  const handleChange = (event: ChangeEvent<{ value: unknown }>) => {
    setStatus(event.target.value as JobStatus);
  };

  React.useEffect(() => {
    setStatus(undefined);
  }, [open]);
  const handleSubmit = async () => {
    onClose();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return onSubmit(status!);
  };
  const availableStatusTransitions = getAvailableStatusTransitions(job.status);

  return (
    <>
      <Dialog
        title="Change job status"
        open={open}
        onClose={onClose}
        content={
          <>
            <DialogContentText>
              Changing the job status will update the workflow and provide
              relevant actions to progress the job.
            </DialogContentText>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="status"
                name="status"
                value={job.status}
                onChange={handleChange}
              >
                {availableStatusTransitions.map((jobStatus) => (
                  <FormControlLabel
                    key={jobStatus.id}
                    value={jobStatus.value}
                    control={<Radio checked={jobStatus.value === status} />}
                    label={<JobsStatus jobStatus={jobStatus.value} />}
                  />
                ))}
              </RadioGroup>
              {status === 'CANCELLED' && (
                <Alert
                  severity="error"
                  sx={(theme) => ({
                    border: 0,
                    marginTop: theme.spacing(spacingSizeMap.XS),
                  })}
                >
                  <TextV2
                    color="error"
                    variant="bodyBold"
                    sx={{ color: 'inherit', fontWeight: 'inherit' }}
                  >
                    Changing the status to &quot;cancelled&quot; will prevent
                    this job from being progressed any further. This change
                    cannot be undone.
                  </TextV2>
                </Alert>
              )}
            </FormControl>
          </>
        }
        primaryButtonText="Change status"
        disablePrimaryButton={!status}
        onSubmit={handleSubmit}
        secondaryButtonText="Cancel"
        onSecondarySubmit={onClose}
      />
      <Snackbar
        open={editJobStatus === 'success'}
        autoHideDuration={6000}
        onClose={resetEditJob}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert elevation={6} onClose={resetEditJob} severity="success">
          Job status updated
        </Alert>
      </Snackbar>
    </>
  );
};

export default JobStatusDialog;
